/**
 * Breadcrumb Component
 * RTL-optimized navigation breadcrumb for Arabic interface
 * Includes sidebar toggle button and year selector
 */
import React, { useState, useRef } from 'react';
import { View, Text, TouchableOpacity, Pressable, StyleSheet, Platform, Modal, FlatList } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Path, Rect } from 'react-native-svg';
import { useRoleNavigation } from '../hooks/useRoleNavigation';
import { useSidebar } from '../contexts/SidebarContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useYear } from '../contexts/YearContext';
import { COLORS, FONTS } from '../constants/config';

// PanelLeftClose icon - sidebar visible, click to hide
const PanelLeftCloseIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="2" />
    <Path d="M9 3V21" stroke={color} strokeWidth="2" />
    <Path d="M16 12L13 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M16 12L13 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// PanelLeftOpen icon - sidebar hidden, click to show
const PanelLeftOpenIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="2" />
    <Path d="M9 3V21" stroke={color} strokeWidth="2" />
    <Path d="M14 12L17 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M14 12L17 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

interface BreadcrumbItem {
  label: string;
  route?: string;
  params?: object;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  showSidebarToggle?: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, showSidebarToggle = true }) => {
  const navigation = useNavigation<any>();
  const { navigateToHome } = useRoleNavigation();
  const { isVisible: isSidebarVisible, toggleSidebar } = useSidebar();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { selectedYear, setSelectedYear, minYear, maxYear } = useYear();
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const yearBtnRef = useRef<View>(null);
  const [yearBtnLayout, setYearBtnLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Theme colors for dark mode
  const themeColors = {
    iconColor: colors.textMuted,
    textColor: colors.textMuted,
    textCurrent: colors.textPrimary,
    chevronColor: isDark ? '#4B5563' : '#D1D5DB',
  };

  // Only show toggle on web platform
  const shouldShowToggle = showSidebarToggle && Platform.OS === 'web';

  // Map screen names to their parent drawer stacks
  const screenToStackMap: Record<string, string> = {
    'UnitGoals': 'UnitGoalsStack',
    'UnitGoalDetail': 'UnitGoalsStack',
    'Goals': 'GoalsStack',
    'GoalDetail': 'GoalsStack',
    'Initiatives': 'InitiativesStack',
    'InitiativeDetail': 'InitiativesStack',
    'KPIs': 'GoalsStack',
    'Services': 'ServicesStack',
    'ServiceDetail': 'ServicesStack',
    'Processes': 'ProcessesStack',
    'ProcessDetail': 'ProcessesStack',
    'SubProcessDetail': 'ProcessesStack',
    'Organizations': 'OrganizationsStack',
    'MyOrganization': 'MyOrganizationStack',
    'Dashboard': 'DashboardStack',
  };

  const handlePress = (item: BreadcrumbItem, index: number) => {
    // Don't navigate if it's the last (current) item
    if (index === items.length - 1) return;

    // Handle home navigation
    if (item.label === t('breadcrumb.home')) {
      navigateToHome();
      return;
    }

    // Navigate to specified route
    if (item.route) {
      // Check if we need to navigate to a different stack first
      const targetStack = screenToStackMap[item.route];
      if (targetStack) {
        // Navigate to the drawer stack, then to the screen within it
        navigation.navigate(targetStack, { screen: item.route, params: item.params || {} });
      } else {
        // Direct navigation for screens in current stack
        navigation.navigate(item.route, item.params || {});
      }
    }
  };

  return (
    <View style={styles.container}>
      {/* Sidebar Toggle Button - appears on the right in RTL */}
      {shouldShowToggle && (
        <TouchableOpacity
          style={styles.toggleButton}
          onPress={toggleSidebar}
          accessibilityLabel={isSidebarVisible ? t('breadcrumb.hideSidebar') : t('breadcrumb.showSidebar')}
        >
          {isSidebarVisible ? (
            <PanelLeftCloseIcon size={20} color={themeColors.iconColor} />
          ) : (
            <PanelLeftOpenIcon size={20} color={themeColors.iconColor} />
          )}
        </TouchableOpacity>
      )}

      <View style={styles.breadcrumbWrapper}>
        {(() => {
          // Truncate breadcrumb when 4+ items: show first, "...", last 2
          const MAX_VISIBLE = 3;
          let displayItems: { item: BreadcrumbItem; originalIndex: number }[] = [];

          if (items.length > MAX_VISIBLE) {
            // First item (Home)
            displayItems.push({ item: items[0], originalIndex: 0 });
            // Ellipsis - navigates to the last collapsed item's route
            const lastCollapsed = items[items.length - 3]; // The item just before the last 2
            displayItems.push({
              item: { label: '...', route: lastCollapsed?.route, params: lastCollapsed?.params },
              originalIndex: -1, // Special marker for ellipsis
            });
            // Last 2 items
            displayItems.push({ item: items[items.length - 2], originalIndex: items.length - 2 });
            displayItems.push({ item: items[items.length - 1], originalIndex: items.length - 1 });
          } else {
            displayItems = items.map((item, i) => ({ item, originalIndex: i }));
          }

          return displayItems.map(({ item, originalIndex }, displayIndex) => {
            const isFirst = displayIndex === 0;
            const isLast = displayIndex === displayItems.length - 1;
            const isClickable = !isLast;

            return (
              <View key={displayIndex} style={styles.itemWrapper}>
                {/* Chevron Separator - shows before item (RTL) */}
                {!isFirst && (
                  <Ionicons
                    name="chevron-back-outline"
                    size={16}
                    color={themeColors.chevronColor}
                    style={styles.chevron}
                  />
                )}

                {/* Breadcrumb Item */}
                <TouchableOpacity
                  onPress={() => handlePress(item, isLast ? items.length - 1 : originalIndex)}
                  disabled={!isClickable}
                  style={styles.item}
                  activeOpacity={isClickable ? 0.7 : 1}
                >
                  {/* Home Icon for first item */}
                  {isFirst && item.label === t('breadcrumb.home') && (
                    <Ionicons
                      name="home-outline"
                      size={16}
                      color={isClickable ? '#A9211F' : themeColors.iconColor}
                      style={styles.homeIcon}
                    />
                  )}

                  <Text
                    style={[
                      styles.text,
                      { color: themeColors.textColor },
                      isClickable && styles.textClickable,
                      isLast && { color: themeColors.textCurrent },
                    ]}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              </View>
            );
          });
        })()}
      </View>

      {/* Year Selector - aligned to the left in RTL (last child in row-reverse) */}
      <View
        ref={yearBtnRef}
        onLayout={() => {
          yearBtnRef.current?.measureInWindow((x, y, width, height) => {
            setYearBtnLayout({ x, y, width, height });
          });
        }}
      >
        <Pressable
          style={({ pressed }) => [
            styles.yearDropdownBtn,
            {
              backgroundColor: colors.card,
              borderColor: colors.cardBorder,
            },
            pressed && { backgroundColor: colors.gray50 },
            Platform.OS === 'web' && { cursor: 'pointer' } as any,
          ]}
          onPress={() => setYearDropdownOpen(!yearDropdownOpen)}
        >
          <ChevronDownIcon size={16} color={colors.textSecondary} />
          <Text style={[styles.yearText, { color: colors.textSecondary }]}>{selectedYear}</Text>
        </Pressable>
      </View>

      {/* Year Dropdown Modal */}
      <Modal
        visible={yearDropdownOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setYearDropdownOpen(false)}
      >
        <Pressable style={styles.yearOverlay} onPress={() => setYearDropdownOpen(false)}>
          <View
            style={[
              styles.yearDropdownList,
              {
                backgroundColor: colors.card,
                borderColor: colors.cardBorder,
                top: yearBtnLayout.y + yearBtnLayout.height + 4,
                left: yearBtnLayout.x,
              },
            ]}
          >
            <FlatList
              data={Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i)}
              keyExtractor={(item) => item.toString()}
              renderItem={({ item }) => (
                <Pressable
                  style={({ pressed }) => [
                    styles.yearDropdownItem,
                    item === selectedYear && { backgroundColor: colors.gray100 },
                    pressed && { backgroundColor: colors.gray50 },
                  ]}
                  onPress={() => {
                    setSelectedYear(item);
                    setYearDropdownOpen(false);
                  }}
                >
                  <Text
                    style={[
                      styles.yearDropdownItemText,
                      { color: colors.textSecondary },
                      item === selectedYear && { color: COLORS.primary, fontFamily: FONTS.medium },
                    ]}
                  >
                    {item}
                  </Text>
                </Pressable>
              )}
              style={{ maxHeight: 250 }}
              initialScrollIndex={Math.max(0, maxYear - selectedYear - 3)}
              getItemLayout={(_, index) => ({ length: 36, offset: 36 * index, index })}
            />
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row-reverse', // RTL - toggle button on right, breadcrumb in middle, year on left
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  toggleButton: {
    padding: 8,
  },
  breadcrumbWrapper: {
    flex: 1,
    flexDirection: 'row-reverse', // RTL
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  itemWrapper: {
    flexDirection: 'row-reverse', // RTL
    alignItems: 'center',
  },
  item: {
    flexDirection: 'row-reverse', // RTL
    alignItems: 'center',
    paddingVertical: 4,
  },
  chevron: {
    marginHorizontal: 8,
  },
  homeIcon: {
    marginLeft: 6,
  },
  text: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  textClickable: {
    color: '#A9211F',
    fontFamily: FONTS.medium,
  },
  textCurrent: {
    color: '#111827',
    fontFamily: FONTS.medium,
  },
  yearDropdownBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 5.5,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  yearText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    lineHeight: 21,
  },
  yearOverlay: {
    flex: 1,
  },
  yearDropdownList: {
    position: 'absolute',
    width: 100,
    borderWidth: 1,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    overflow: 'hidden',
  },
  yearDropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  yearDropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
});

export default Breadcrumb;
